import {
  Button,
  Dialogue,
  IconButton,
  InputStepper,
  Modal,
  Popover,
  Text,
  WithToggle
} from '@albert-io/atomic';
import cx from 'classnames';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {useTracking} from 'react-tracking';

import {useContentDiscoveryStore} from 'client/components/ContentDiscovery/store/ContentDiscoveryStore.react';
import {history} from 'client/history';
import {getModelForResourceType} from 'resources/modelRegistry';

import './assign-modal.scss';

const AssignModal = ({onClose, resource, type}) => {
  const {Track, trackEvent} = useTracking({
    component: 'Smart Assign modal',
    feature: 'Smart Assign'
  });
  const maxQuestions = resource.getMeta().getCountOfQuestions();
  const minQuestions = resource.getMeta().getMinSizeQuestionSet();
  const [questionNum, setQuestionNum] = useState(maxQuestions < 10 ? maxQuestions : 10);

  const {setNumSmartAssignQuestions, setLegacyItemId, setLegacyItemType} =
    // oxlint-disable-next-line react/react-compiler -- zustand store static API (getState/subscribe), not a hook call
    useContentDiscoveryStore.getState().draft;

  const minError = questionNum < minQuestions;
  const maxError = questionNum > maxQuestions;
  const decimalError = questionNum % 1 !== 0;
  const hasError = maxError || minError || decimalError;

  let errorMessage = `Must be between ${minQuestions} and ${maxQuestions}`;
  if (decimalError) errorMessage = `Number of questions must be a whole number`;

  const onContinue = async () => {
    trackEvent({event: 'click', type: 'button', content: 'Continue'});

    if (questionNum === 0) {
      history.pushState(null, `/assignment-builder/create-assignment`);
    } else {
      setNumSmartAssignQuestions(questionNum);
      setLegacyItemId(resource.getId());
      setLegacyItemType(type);
      history.pushState(null, `/assignment-builder/create-assignment/settings`);
    }
  };

  const handleClose = (e) => {
    trackEvent({event: 'click out', type: 'modal', content: null});
    onClose(e);
  };

  const onCancel = (e) => {
    trackEvent({event: 'click', type: 'button', content: 'Cancel'});
    onClose(e);
  };

  return (
    <Track>
      <Modal ariaLabel='Select assignment parameters' handleClose={handleClose} role='dialog'>
        {() => (
          <Dialogue size='s' inModal hideCloseBtn paddingLevel={3} alignTitle='center'>
            <Text bold as='p' className='text-center'>
              How many questions would <br />
              you like to assign?
            </Text>
            <Dialogue.Body className='mt-x6 flex flex-col items-center' align='center'>
              <InputStepper
                value={questionNum}
                max={maxQuestions}
                min={minQuestions}
                error={hasError}
                onChange={setQuestionNum}
                errorMessage={errorMessage}
                ariaLabel='input-stepper'
              />
            </Dialogue.Body>
            <Dialogue.BtnGroup>
              <Button color='secondary' onClick={onCancel}>
                Cancel
              </Button>
              <Button onClick={onContinue} disabled={hasError}>
                Continue
              </Button>
            </Dialogue.BtnGroup>
          </Dialogue>
        )}
      </Modal>
    </Track>
  );
};

AssignModal.propTypes = {
  type: PropTypes.oneOf(['folder', 'guide']).isRequired,
  onClose: PropTypes.func,
  resource: PropTypes.oneOfType([
    PropTypes.instanceOf(getModelForResourceType('template_v1')),
    PropTypes.instanceOf(getModelForResourceType('guide_level_v2'))
  ]).isRequired
};

export default AssignModal;

export const SmartAssignTooltip = ({className, leftContent}) => {
  const iconRef = React.createRef();
  return (
    <span className={cx('flex items-center', className)}>
      {leftContent}
      <WithToggle className='ml-auto'>
        {({on, onClick: togglePopover, onBlur, onMouseEnter, onMouseLeave}) => (
          <span onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onBlur={onBlur}>
            <IconButton
              size='s'
              icon={['far', 'question-circle']}
              label='More info'
              onClick={togglePopover}
              ref={iconRef}
            />
            {on && (
              <Popover targetRef={iconRef} className='assign-modal__tooltip' position='right-start'>
                <Text as='p' color='primary-inverse' size='xs'>
                  Creates an assignment based on the amount of questions you’d like to use from the
                  entire question bank. <b>Note: Question order will be randomized.</b>{' '}
                </Text>
              </Popover>
            )}
          </span>
        )}
      </WithToggle>
    </span>
  );
};

SmartAssignTooltip.propTypes = {
  className: PropTypes.string,
  leftContent: PropTypes.node
};

export const SmartAssignDisabled = ({className}) => {
  return (
    <div className={`flex flex-col ${className}`}>
      <Text>Smart Assign</Text>
      <Text size='xs' color='secondary' className='w-[187px] whitespace-pre-line'>
        Smart assign is not available for folders with archived questions.
      </Text>
    </div>
  );
};
